import { gql } from '@apollo/client';

/**
 * Device-platform integration operations — Zoom Rooms (Phase A), Teams /
 * Webex / Meet (Phase B placeholders).
 *
 * Spec: docs/specs/19-optiedge-mdm/zoom-rooms/07-zoom-rooms-integration-spec.md §4
 *
 * The resolver surface is DELIBERATELY generic — zero Zoom-specific mutation
 * names. The adapter the backend routes to is selected by `platformId` arg.
 * This lets Teams / Webex / Meet land later with zero schema churn.
 *
 * Operation names MUST match the strings used in `refetchQueries`. The canon:
 *
 *   query  ListPlatformConnections   → refetch after connect/disconnect/sync
 *   query  Devices                   → refetch after activateSignage (covers Zoom Rooms page list)
 *
 * Secret tokens (access/refresh/clientSecret) are NEVER selected on
 * `PlatformConnection` — backend redacts them on the resolver response and
 * the GraphQL type doesn't declare them. Teams `clientId` + `tenantId` ARE
 * selected: public Azure GUIDs needed to pre-fill the Manage SP edit dialog.
 */

// --- Queries ---

export const LIST_PLATFORM_CONNECTIONS = gql`
  query ListPlatformConnections {
    listPlatformConnections {
      platformId
      status
      connectionName
      connectedAt
      lastSyncAt
      lastSyncError {
        code
        message
        at
      }
      platformMeta
      clientId
      tenantId
    }
  }
`;

// mdmDevicesForPlatform — list rooms surfaced from one platform's sync (the
// per-platform Connect page query). Returns BOTH unactivated and activated
// rooms in one pass — `signageActivatedAt` distinguishes them.
//
// Backed by `mdm-devices` collection (smallapp upserts here). Distinct from
// the `Devices` query which targets the `devices` collection (signage
// licenses + paired hardware).
//
// Phase 2 (Google Meet) extends `platformMeta` with 13 typed Google fields
// (chromeOsDeviceId, serialNumber, orgUnitPath, annotatedUser,
// annotatedLocation, annotatedAssetId, resourceId, resourceEmail,
// resourceCategory, buildingId, buildingName, floorName, capacity). Other
// platforms continue to populate their own subset via the same scalar JSON
// field — selection is wide (no per-platform branches) since Apollo lazy-
// fetches on demand and unread fields don't transit the wire if absent.
export const MDM_DEVICES_FOR_PLATFORM = gql`
  query MdmDevicesForPlatform($platformId: PlatformId!) {
    mdmDevicesForPlatform(platformId: $platformId) {
      _id
      name
      platform
      managementCapability
      manufacturer
      model
      os
      osVersion
      firmwareVersion
      lastHeartBeat
      roomStatus
      availableCommands
      externalRef
      rawAttributes
      signageActivatedAt
      devicesId
      signageUrl
      currentAssetId
      currentPlaylistId
      currentScheduleId
      currentType
      currentName
      createdAt
      updatedAt
      platformMeta
      lastPushedAt
      lastPushStatus
      lastPushError
      signageVerifiedAt
      signageVerifyStatus
      signageCleanupFailedAt
      signageCleanupFailReason
    }
  }
`;

// refreshPlatformDevice — drawer-open auto-refresh. Fetches /rooms/{id} +
// /rooms/{id}/devices + /rooms/{id}/issues against the vendor API in one
// pass, persists fresh fields to mdm-devices, and publishes a pubsub UPDATE
// so the Connect page refetches.
export const REFRESH_PLATFORM_DEVICE = gql`
  mutation RefreshPlatformDevice($mdmDeviceId: ID!) {
    refreshPlatformDevice(mdmDeviceId: $mdmDeviceId) {
      ok
      error
    }
  }
`;

// --- Mutations ---

// connectPlatform returns the vendor auth URL (e.g. Zoom's /oauth/authorize
// URL with our client_id + scopes + signed state). Frontend opens it in a
// new tab; vendor redirects to /integrations/<vendor>/callback on open-api,
// which completes the OAuth exchange and 302s back to
// /app/mdm/connect/<vendor>?connected=1.
export const CONNECT_PLATFORM = gql`
  mutation ConnectPlatform($platformId: PlatformId!) {
    connectPlatform(platformId: $platformId)
  }
`;

// disconnectPlatform wipes tokens, sets status='disconnected', soft-deletes
// unlicensed mdm-devices rows. Licensed rooms remain (they still bill); their
// drawer shows a "Zoom disconnected" badge until reconnect. Refetch
// ListPlatformConnections + Devices after.
export const DISCONNECT_PLATFORM = gql`
  mutation DisconnectPlatform($platformId: PlatformId!) {
    disconnectPlatform(platformId: $platformId)
  }
`;

// syncPlatformConnection is a fire-and-forget — the backend publishes
// SYNC_REQUESTED on SCIO_PUBSUB_CHANNEL, the sync smallapp consumes it, and
// UI refreshes via useRealtimeSubscription when pubsub events land. Button
// surfaces optimistic "Syncing..." state via local component state, NOT
// Apollo optimistic response.
export const SYNC_PLATFORM_CONNECTION = gql`
  mutation SyncPlatformConnection($platformId: PlatformId!) {
    syncPlatformConnection(platformId: $platformId)
  }
`;

// activateSignage takes the vendor-facing room IDs (Zoom room IDs in Phase A),
// per-room runs the 6-step transaction, returns partial-success shape.
// Refetch Devices + ListPlatformConnections after.
//
// InsufficientLicensesException (returned as GraphQL error) is the signal
// for the frontend to open the subscription modal rather than show a toast.
//
// MDM Phase 1 (BG6): per-room `failed[i].code` carries a discriminated
// `ActivateSignageErrorCode` ('ALREADY_ACTIVATED' | 'LICENSE_RACE_LOST' |
// 'UNKNOWN'). Top-level race losses surface as a GraphQL error with code
// `LICENSE_RACE_LOST` (extracted via `getGraphQLErrorCode`).
export const ACTIVATE_SIGNAGE = gql`
  mutation ActivateSignage($externalDeviceIds: [ID!]!) {
    activateSignage(externalDeviceIds: $externalDeviceIds) {
      activated {
        externalDeviceId
        deviceId
        signageUrl
      }
      failed {
        externalDeviceId
        reason
        code
      }
    }
  }
`;

// deactivateSignage releases each room's license (deletes the linked Screen →
// the active-room count drops) and runs the per-room local teardown. Returns a
// partial-success shape mirroring activateSignage. Does NOT change the
// reservation cap — the frontend lowers the cap separately via
// `setRoomsLicenseAllocation` AFTER the rooms are deactivated (B6 deactivate
// picker in LicenseAllocationDialog). Refetch the device + connection +
// license-pool queries after.
//
// Per-room `failed[i].code`: NOT_ACTIVATED (room had no license to release) |
// LOCAL_TEARDOWN_FAILED (license released but local cleanup failed) | UNKNOWN.
export const DEACTIVATE_SIGNAGE = gql`
  mutation DeactivateSignage($externalDeviceIds: [ID!]!) {
    deactivateSignage(externalDeviceIds: $externalDeviceIds) {
      deactivated {
        externalDeviceId
        deviceId
      }
      failed {
        externalDeviceId
        reason
        code
      }
    }
  }
`;

// MDM Phase 1 (BG8): sync-health rollup. Drives the Devices page subtitle
// ("last platform sync 14s ago") + the per-vendor "Sync delayed" banner.
// pollInterval is set on the consumer side (useAccountMdmStats) so the
// relative-time text stays fresh without WS subscriptions.
export const GET_ACCOUNT_MDM_STATS = gql`
  query GetAccountMdmStats {
    accountMdmStats {
      lastSyncAt
      connectedVendorCount
      connectionSyncHealth {
        platformId
        lastSyncAt
        isStale
      }
    }
  }
`;

// MDM Phase 2 (BG3'): rooms whose disconnect-time vendor-side cleanup
// failed. Drives the Connect Hub inline warning + the disconnect-confirm
// post-summary. Background cron retries hourly within a 24h budget; the
// user can also call DISMISS_PLATFORM_CLEANUP after manually cleaning up
// in the vendor portal.
export const PENDING_PLATFORM_CLEANUP = gql`
  query PendingPlatformCleanup {
    pendingPlatformCleanup {
      count
      samples {
        mdmDeviceId
        roomName
        platformId
        failedAt
        reason
      }
    }
  }
`;

export const DISMISS_PLATFORM_CLEANUP = gql`
  mutation DismissPlatformCleanup($mdmDeviceId: ID!) {
    dismissPlatformCleanup(mdmDeviceId: $mdmDeviceId)
  }
`;

// MDM Phase 2 (BG9b): vendor-room drawer Activity feed. Merges three event
// streams (audit-log + command-execution + device_platform_audits) server-side
// by timestamp. `operationKey` is an i18n key; `params` is the lookup payload
// for `t(operationKey, params)`.
export const DEVICE_ACTIVITY_FEED = gql`
  query DeviceActivityFeed($mdmDeviceId: ID!, $limit: Int) {
    deviceActivityFeed(mdmDeviceId: $mdmDeviceId, limit: $limit) {
      id
      kind
      at
      actorUserId
      actorDisplay
      operationKey
      params
      status
    }
  }
`;

// platformApiCommand is the generic dispatcher for every platform-api
// operation (syncNow, openAdminDeepLink, reboot, restartApp, removeSignage,
// fetchIssues, ...). MdmActionRow's `{type: 'platform-api', operation}`
// handler calls this; the mdm.resolver on the backend routes it through
// DevicePlatformsService.platformApiCommand → adapter.<operation>.
export const PLATFORM_API_COMMAND = gql`
  mutation PlatformApiCommand($deviceId: ID!, $operation: String!, $args: JSONObject) {
    platformApiCommand(deviceId: $deviceId, operation: $operation, args: $args) {
      ok
      data
      error
    }
  }
`;

// readSignageSettings — typed wrapper over platformApiCommand for fetching the
// vendor's current Signage Management config for a Zoom Room (layout, display
// period, mute, enableSignage). Resolver bakes the operation literal; UI only
// passes the deviceId. Spec 12 §8.3.
export const READ_ZOOM_SIGNAGE_SETTINGS = gql`
  mutation ReadZoomSignageSettings($deviceId: ID!) {
    platformApiCommand(deviceId: $deviceId, operation: "readSignageSettings") {
      ok
      data
      error
    }
  }
`;

// updateSignageSettings — typed wrapper over platformApiCommand for patching
// the vendor's Signage Management config for a Zoom Room. `args` is the
// partial settings patch JSON: `{ layout?, displayPeriod?: {start, stop},
// mute?, enableSignage? }`. Resolver bakes the operation literal. Spec 12
// §8.3.
export const UPDATE_ZOOM_SIGNAGE_SETTINGS = gql`
  mutation UpdateZoomSignageSettings($deviceId: ID!, $args: JSONObject!) {
    platformApiCommand(deviceId: $deviceId, operation: "updateSignageSettings", args: $args) {
      ok
      data
      error
    }
  }
`;

// resetSignageSessions wipes all active player sessions for one Room — admin
// escape hatch for "URL leaked, force everyone out". Live players see the
// rejection UI on next 30s heartbeat; the Zoom Room reclaims its slot if free.
// Spec 09 §"Session lock (Option C)".
export const RESET_SIGNAGE_SESSIONS = gql`
  mutation ResetSignageSessions($mdmDeviceId: ID!) {
    resetSignageSessions(mdmDeviceId: $mdmDeviceId)
  }
`;

// --- Cisco Webex Rooms — per-room signage settings (Phase A) ---------------
//
// Typed wrappers over the generic `platformApiCommand` dispatcher — the
// resolvers route through `WebexRoomsAdapter.{readSignageSettings,
// updateSignageSettings}` against Cisco's Device Configurations API
// (`Standby.Signage.*` xConfig nodes). Webex gets dedicated `read*` /
// `update*` resolvers (rather than overloaded `platformApiCommand`) because
// the input + response shapes are typed enums (`'Interactive' |
// 'NonInteractive'`, `'On' | 'Off'`) which need a concrete
// `WebexSignageSettingsInput!` GraphQL type rather than `JSONObject`.
//
// Operation names match the strings used in `refetchQueries` — keep
// `ReadWebexSignageSettings` exact (case-sensitive). After
// `UpdateWebexSignageSettings` succeeds the drawer card refetches itself
// rather than refetching the parent device list (settings are per-device,
// not list-visible).
//
// Spec: docs/specs/19-optiedge-mdm/webex-rooms/01-webex-rooms-integration-spec.md §5.7

export const READ_WEBEX_SIGNAGE_SETTINGS = gql`
  query ReadWebexSignageSettings($mdmDeviceId: ID!) {
    readWebexSignageSettings(mdmDeviceId: $mdmDeviceId) {
      interactionMode
      refreshIntervalMinutes
      audioMuted
      signageEnabled
      signageUrl
      webEngineMode
    }
  }
`;

export const UPDATE_WEBEX_SIGNAGE_SETTINGS = gql`
  mutation UpdateWebexSignageSettings($mdmDeviceId: ID!, $input: WebexSignageSettingsInput!) {
    updateWebexSignageSettings(mdmDeviceId: $mdmDeviceId, input: $input) {
      interactionMode
      refreshIntervalMinutes
      audioMuted
      signageEnabled
      signageUrl
      webEngineMode
    }
  }
`;

// --- Microsoft Teams Rooms — BYO Service Principal (Phase A) ---------------
//
// Teams Rooms uses customer-pasted SP credentials instead of OAuth (no
// `connectPlatform` round-trip for Teams). `testPlatformServicePrincipal`
// validates against Microsoft Graph SYNCHRONOUSLY before the customer clicks
// Save — UI gates Save on `ok === true`. `savePlatformServicePrincipal`
// re-runs the same validation server-side, encrypts the client secret with
// OPS_TOKEN_ENCRYPTION_KEY, upserts the connection (unique on
// {accountId, platformId}), and publishes SYNC_REQUESTED.
//
// Spec: docs/specs/19-optiedge-mdm/ms-teams-rooms/01-ms-teams-rooms-integration-spec.md §4

export const TEST_PLATFORM_SERVICE_PRINCIPAL = gql`
  mutation TestPlatformServicePrincipal($platformId: PlatformId!, $sp: ServicePrincipalInput!) {
    testPlatformServicePrincipal(platformId: $platformId, sp: $sp) {
      ok
      error
      errorMessage
      tenantDisplayName
    }
  }
`;

export const SAVE_PLATFORM_SERVICE_PRINCIPAL = gql`
  mutation SavePlatformServicePrincipal($platformId: PlatformId!, $sp: ServicePrincipalInput!) {
    savePlatformServicePrincipal(platformId: $platformId, sp: $sp) {
      platformId
      status
      connectionName
      connectedAt
      lastSyncAt
      lastSyncError {
        code
        message
        at
      }
      platformMeta
      clientId
      tenantId
    }
  }
`;

// --- Cisco Webex Rooms — Verify Connection (Service App Stage 2) -----------
//
// Replaces the OAuth callback flow Zoom uses. Webex Service Apps require:
//   Stage 1 — admin clicks Approve in Cisco Control Hub (URL returned by
//             `connectPlatform(platformId='webex-rooms')`).
//   Stage 2 — customer pastes their Webex Org ID + clicks Verify Connection.
//             THIS mutation. Backend mints per-org tokens via Cisco's
//             `/v1/applications/{appId}/token` endpoint, encrypts via
//             TokenCryptoService, upserts the connection, publishes
//             SYNC_REQUESTED. Returns the persisted PlatformConnection so the
//             frontend cache can flip from `notConnected` → `connected`.
//
// Field selection mirrors LIST_PLATFORM_CONNECTIONS for cache consistency.
//
// Spec: docs/specs/19-optiedge-mdm/research/webex-deep-dive.md §2b
//       docs/specs/19-optiedge-mdm/webex-rooms/IDENTITY_MODEL.md
export const VERIFY_WEBEX_CONNECTION = gql`
  mutation VerifyWebexConnection($input: VerifyWebexConnectionInput!) {
    verifyWebexConnection(input: $input) {
      platformId
      status
      connectionName
      connectedAt
      lastSyncAt
      lastSyncError {
        code
        message
        at
      }
      platformMeta
      clientId
      tenantId
    }
  }
`;

// --- Google Meet — BYO Service Account (Phase 2) ---------------------------
//
// Google Meet hardware uses a different auth shape than Zoom's per-user OAuth
// or Teams' BYO Service Principal: the customer registers their own GCP
// Service Account, grants Domain-Wide Delegation, and pastes 4 fields into
// OptiSigns. Backend signs JWTs per call (no refresh-token cron).
//
// `testPlatformServiceAccount` validates against Google's token endpoint +
// `chromeosdevices.list?maxResults=1` SYNCHRONOUSLY before persist — UI gates
// Save on `ok === true`. `savePlatformServiceAccount` re-runs the same check
// server-side, encrypts the full SA JSON via OPS_TOKEN_ENCRYPTION_KEY,
// upserts the connection (unique on {accountId, platformId}), and publishes
// SYNC_REQUESTED. The Marketplace path reuses the existing `connectPlatform`
// OAuth flow.
//
// Spec: docs/specs/19-optiedge-mdm/google-meet/02-spec-phase-2.md §5.2

export const TEST_PLATFORM_SERVICE_ACCOUNT = gql`
  mutation TestPlatformServiceAccount($platformId: PlatformId!, $sa: ServiceAccountInput!) {
    testPlatformServiceAccount(platformId: $platformId, sa: $sa) {
      ok
      error
      errorMessage
      workspaceDomain
      primaryDomain
      deviceCount
    }
  }
`;

export const SAVE_PLATFORM_SERVICE_ACCOUNT = gql`
  mutation SavePlatformServiceAccount($platformId: PlatformId!, $sa: ServiceAccountInput!) {
    savePlatformServiceAccount(platformId: $platformId, sa: $sa) {
      ok
      error
      errorMessage
      connectionId
      workspaceDomain
      connection {
        platformId
        status
        connectionName
        connectedAt
        lastSyncAt
        lastSyncError {
          code
          message
          at
        }
        platformMeta
        # Tokens are NEVER selected — backend redacts; spec §5.2.
      }
    }
  }
`;

// --- Zoom Rooms — BYO Server App (Phase 2) ---------------------------------
//
// Zoom Server-to-Server OAuth ("Server App") path lets the customer paste
// 3 fields (Account ID + Client ID + Client Secret) instead of round-tripping
// through Zoom's Marketplace consent screen. `testPlatformZoomServerApp`
// validates against Zoom SYNCHRONOUSLY before the customer clicks Save — UI
// gates Save on `ok === true`. `savePlatformZoomServerApp` re-runs the same
// validation server-side, encrypts the client secret with
// OPS_TOKEN_ENCRYPTION_KEY, upserts the connection (unique on
// {accountId, platformId}), and publishes SYNC_REQUESTED.
//
// Spec: docs/specs/19-optiedge-mdm/zoom-rooms/13-zoom-rooms-phase-2-server-app-and-ux.md §5

export const TEST_PLATFORM_ZOOM_SERVER_APP = gql`
  query TestPlatformZoomServerApp($input: ZoomServerAppInput!) {
    testPlatformZoomServerApp(input: $input) {
      ok
      error
      errorMessage
      connectedAs
      roomCount
    }
  }
`;

export const SAVE_PLATFORM_ZOOM_SERVER_APP = gql`
  mutation SavePlatformZoomServerApp($input: ZoomServerAppInput!) {
    savePlatformZoomServerApp(input: $input) {
      ok
      error
      errorMessage
      connectedAs
      roomCount
      connection {
        platformId
        status
        connectionName
        connectedAt
        lastSyncAt
        lastSyncError {
          code
          message
          at
        }
        platformMeta
      }
    }
  }
`;

// --- Zoom Rooms — Activation pre-flight (Phase 2) --------------------------
//
// `getZoomSignageState(externalDeviceIds)` — called by ActivateSignageDialog
// when `dialogContext.platformId === 'zoom-rooms'`. Backend fans out
// concurrently to Zoom's GET /rooms/{id}/digital_signage and trims the
// response to the rows below. UI shows the warning panel only when ≥ 1 row
// has a non-empty `items` array.
//
// Spec: docs/specs/19-optiedge-mdm/zoom-rooms/13-zoom-rooms-phase-2-server-app-and-ux.md §6

export const GET_ZOOM_SIGNAGE_STATE = gql`
  query GetZoomSignageState($externalDeviceIds: [ID!]!) {
    getZoomSignageState(externalDeviceIds: $externalDeviceIds) {
      externalDeviceId
      items {
        name
        contentId
      }
    }
  }
`;

// --- Google Meet — Org Unit filter (Phase 2) -------------------------------
//
// Distinct OU paths from the current account's `mdm_devices` collection
// (filtered to `platform === 'google-meet-room'`). Apollo client caches the
// result for 5 min. Lazy-fired on `OrgUnitFilterDropdown` open (not on page
// mount) — saves a query for accounts without Google connections.
//
// Spec: docs/specs/19-optiedge-mdm/google-meet/02-spec-phase-2.md §5.3

export const DISTINCT_ORG_UNIT_PATHS = gql`
  query DistinctOrgUnitPaths {
    distinctOrgUnitPaths
  }
`;
